import React from "react";
import { Button } from "@/components";
import {
  Edit,
  Trash2,
  ArrowLeft,
  Calendar,
  Image as ImageIcon,
  Video,
  Target,
  Link as LinkIcon,
  HelpCircle,
} from "lucide-react";
import type { Campaign } from "../interfaces";

interface CampaignDetailProps {
  campaign: Campaign;
  onEdit: (campaign: Campaign) => void;
  onDelete: (campaignId: number) => void;
  onBack: () => void;
}

export const CampaignDetail: React.FC<CampaignDetailProps> = ({
  campaign,
  onEdit,
  onDelete,
  onBack,
}) => {
  const handleDelete = () => {
    onDelete(campaign.id);
  };

  const duration =
    campaign.config?.duration &&
    `${campaign.config.duration.periodCount} ${campaign.config.duration.period}`;

  return (
    <div className="max-w-5xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={onBack}
              title="Back to list"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-3xl font-bold">{campaign.title}</h1>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onEdit(campaign)}>
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </Button>
          </div>
        </div>
        <div className="flex items-center gap-4 text-sm text-muted-foreground flex-wrap">
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            <span>Created: {new Date(campaign.createdAt).toLocaleDateString()}</span>
          </div>
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            <span>Updated: {new Date(campaign.updatedAt).toLocaleDateString()}</span>
          </div>
          {duration && (
            <span className="px-2 py-1 bg-muted rounded text-xs font-medium">
              Duration: {duration}
            </span>
          )}
          {campaign.strategyIds && campaign.strategyIds.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {campaign.strategyIds.map((id) => (
                <span
                  key={id}
                  className="px-2 py-1 bg-primary/10 text-primary rounded text-xs font-medium"
                >
                  Plan #{id}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Description */}
      <div className="bg-card border border-input rounded-lg p-6 mb-6">
        <h2 className="text-xl font-semibold mb-3">Description</h2>
        <p className="text-muted-foreground whitespace-pre-wrap">
          {campaign.description}
        </p>
      </div>

      {/* Goals */}
      {campaign.config?.goals && campaign.config.goals.length > 0 && (
        <div className="bg-card border border-input rounded-lg p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Target className="h-5 w-5" />
            <h2 className="text-xl font-semibold">Goals</h2>
          </div>
          <ul className="space-y-2">
            {campaign.config.goals.map((goal, index) => (
              <li key={index} className="flex items-start gap-2">
                <span className="text-primary mt-1">•</span>
                <span className="text-muted-foreground flex-1">{goal}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* CTAs */}
      {campaign.config?.cta && campaign.config.cta.length > 0 && (
        <div className="bg-card border border-input rounded-lg p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <LinkIcon className="h-5 w-5" />
            <h2 className="text-xl font-semibold">Call to Action</h2>
          </div>
          <div className="space-y-4">
            {campaign.config.cta.map((item, index) => (
              <div
                key={index}
                className="border-l-4 border-primary pl-4 py-2"
              >
                <p className="font-medium">{item.goal}</p>
                <a
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-primary hover:underline break-all"
                >
                  {item.url}
                </a>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* FAQs */}
      {campaign.config?.faqs && campaign.config.faqs.length > 0 && (
        <div className="bg-card border border-input rounded-lg p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <HelpCircle className="h-5 w-5" />
            <h2 className="text-xl font-semibold">
              FAQs ({campaign.config.faqs.length})
            </h2>
          </div>
          <div className="space-y-4">
            {campaign.config.faqs.map((faq, index) => (
              <div key={index} className="border-l-4 border-primary pl-4">
                <h3 className="font-semibold mb-2">{faq.question}</h3>
                <p className="text-muted-foreground whitespace-pre-wrap">
                  {faq.answer}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Images */}
      {campaign.images && campaign.images.length > 0 && (
        <div className="bg-card border border-input rounded-lg p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <ImageIcon className="h-5 w-5" />
            <h2 className="text-xl font-semibold">
              Images ({campaign.images.length})
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {campaign.images.map((image, index) => (
              <div
                key={index}
                className="aspect-video rounded-lg overflow-hidden bg-muted group cursor-pointer hover:ring-2 hover:ring-primary transition-all"
              >
                <img
                  src={image}
                  alt={`${campaign.title} image ${index + 1}`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                  onClick={() => window.open(image, "_blank")}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Videos */}
      {campaign.videos && campaign.videos.length > 0 && (
        <div className="bg-card border border-input rounded-lg p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Video className="h-5 w-5" />
            <h2 className="text-xl font-semibold">
              Videos ({campaign.videos.length})
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {campaign.videos.map((video, index) => (
              <div
                key={index}
                className="aspect-video rounded-lg overflow-hidden bg-muted"
              >
                {video.includes("youtube.com/embed") ||
                video.includes("youtu.be") ? (
                  <iframe
                    src={video}
                    title={`${campaign.title} video ${index + 1}`}
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                ) : (
                  <video src={video} controls className="w-full h-full">
                    Your browser does not support the video tag.
                  </video>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty state */}
      {(!campaign.config?.goals || campaign.config.goals.length === 0) &&
        (!campaign.config?.cta || campaign.config.cta.length === 0) &&
        (!campaign.config?.faqs || campaign.config.faqs.length === 0) &&
        (!campaign.images || campaign.images.length === 0) &&
        (!campaign.videos || campaign.videos.length === 0) && (
          <div className="bg-card border border-input rounded-lg p-12 text-center">
            <p className="text-muted-foreground">
              No additional content for this campaign.
            </p>
          </div>
        )}
    </div>
  );
};
